/**
 * 长按手势的纯状态机（任务 11a；零 DOM 依赖，可单测）。DOM 接线 hook 在 11b。
 *
 * 语义（对齐移动端通用长按 + 旧版 Flutter 长按菜单触感）：
 * - 按下后原地停留满 LONG_PRESS_MS 触发 onTrigger（带按下坐标，供菜单定位/
 *   高亮气泡）；期间任一条件破坏即取消：
 *   - 位移超过 MOVE_SLOP（用户在滚动列表，滚动优先）；
 *   - 第二根手指按下（捏合/双指手势不触发菜单）；
 *   - 提前抬手（算普通点击，由 click 路径处理）；
 *   - pointercancel（浏览器手势接管）。
 * - 触发后本根手指的 up 返回 true，接线层据此抑制紧随的 click 与
 *   contextmenu（iOS 长按同时派发 contextmenu 的双触发问题）。
 * - 计时器注入（schedule）便于单测；默认 setTimeout。
 */

/** 长按触发时长（ms）。Flutter kLongPressTimeout 同值。 */
export const LONG_PRESS_MS = 500;
/** 位移容差（px）：超过即认定是滚动，取消长按。 */
export const MOVE_SLOP = 10;

export interface LongPressCallbacks {
  /** 长按达成。x/y = 按下时坐标（client 坐标系，菜单定位用）。 */
  onTrigger(x: number, y: number): void;
}

export type CancelTimer = () => void;
export type ScheduleFn = (fn: () => void, ms: number) => CancelTimer;

export interface LongPressGesture {
  down(pointerId: number, x: number, y: number): void;
  move(pointerId: number, x: number, y: number): void;
  /** 返回 true = 本根手指已触发过长按，调用方应抑制后续 click/contextmenu。 */
  up(pointerId: number): boolean;
  /** pointercancel / 组件卸载。 */
  cancel(): void;
}

const defaultSchedule: ScheduleFn = (fn, ms) => {
  const id = setTimeout(fn, ms);
  return () => clearTimeout(id);
};

export function createLongPressGesture(
  callbacks: LongPressCallbacks,
  schedule: ScheduleFn = defaultSchedule,
): LongPressGesture {
  let phase: 'idle' | 'waiting' | 'fired' = 'idle';
  let id = -1;
  let startX = 0;
  let startY = 0;
  let cancelTimer: CancelTimer | null = null;

  function reset(): void {
    if (cancelTimer !== null) {
      cancelTimer();
      cancelTimer = null;
    }
    phase = 'idle';
    id = -1;
  }

  return {
    down(pointerId, x, y) {
      if (phase !== 'idle') {
        // 第二根手指：无论等待中还是已触发，都终止当前手势。
        reset();
        return;
      }
      phase = 'waiting';
      id = pointerId;
      startX = x;
      startY = y;
      cancelTimer = schedule(() => {
        cancelTimer = null;
        phase = 'fired';
        callbacks.onTrigger(startX, startY);
      }, LONG_PRESS_MS);
    },
    move(pointerId, x, y) {
      if (phase !== 'waiting' || pointerId !== id) return;
      if (Math.abs(x - startX) > MOVE_SLOP || Math.abs(y - startY) > MOVE_SLOP) reset();
    },
    up(pointerId) {
      if (pointerId !== id || phase === 'idle') return false;
      const fired = phase === 'fired';
      reset();
      return fired;
    },
    cancel() {
      reset();
    },
  };
}
